<template>
  <div class="navbar-main-menu">
    <div class="container px-0">
      <div class="row p-0 justify-center">
        <div class="navbar-main-menu-col col-9">
          <div class="col-head row m-0 p-0">
            <div class="embed-responsive">
              <Component :is="parents[1].svg" />
            </div>
            <div class="ml-4">
              <p class="font-bold mt-O mb-0 ml-auto mr-auto relative head-title">
                {{ parents[1].title }}
              </p>
              <p class="mb-0 relative ml-auto mr-auto head-subtitle">
                <span class="hidden lg:block">{{ parents[1].desktopText }}</span>
                <span class="lg:hidden">{{ parents[1].mobileText }}</span>
              </p>
            </div>
          </div>
          <ul class="row p-0 m-0 list-unstyled mt-4">
            <li
              v-for="(item, key) in children[1].items"
              :key="key"
              class="col-4 mb-3 xxl:mb-4"
            >
              <div class="row menu-item">
                <!-- image -->
                <div class="menu-item-img">
                  <div class="embed-responsive embed-responsive-4by3 mb-0 rounded-sm">
                    <RemoteImage
                      :src="`https://img.mix.it/general_assets/categories/${item.image}`"
                      img-class="embed-responsive-item image-contain"
                      :alt="item.text"
                    />
                  </div>
                </div>
                <!-- text -->
                <div class="ml-3">
                  <router-link
                    :to="{ name: item.name, params: { permalink: item.permalink } }"
                    class="block stretched-link underline-hover leading-sm menu-item-link"
                  >
                    {{ item.text }}
                  </router-link>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="navbar-main-menu-col col-3">
          <div class="col-head row m-0 p-0">
            <div class="embed-responsive">
              <Component :is="parents[0].svg" />
            </div>
            <div class="ml-4">
              <p class="font-bold mt-O mb-0 ml-auto mr-auto relative head-title">
                {{ parents[0].title }}
              </p>
              <p class="mb-0 relative ml-auto mr-auto head-subtitle">
                <span class="hidden lg:block">{{ parents[0].desktopText }}</span>
                <span class="lg:hidden">{{ parents[0].mobileText }}</span>
              </p>
            </div>
          </div>
          <ul class="row p-0 m-0 list-unstyled mt-4">
            <li
              v-for="(item, key) in children[0].items"
              :key="key"
              class="col-12 mb-3 xxl:mb-4"
            >
              <div class="row menu-item">
                <!-- image -->
                <div class="menu-item-img">
                  <div class="embed-responsive mb-0 rounded-sm">
                    <RemoteImage
                      :src="`https://img.mix.it/general_assets/categories/${item.image}`"
                      img-class="embed-responsive-item relative image-contain"
                      :alt="item.text"
                    />
                  </div>
                </div>
                <!-- text -->
                <div class="ml-3">
                  <router-link
                    v-if="item.kind === 'mixit_bars' || item.kind === 'fruit_bags'"
                    :to="{ name: 'productsPath', params: { permalink: item.permalink } }"
                    class="block stretched-link underline-hover leading-sm menu-item-link"
                  >
                    {{ item.text }}
                  </router-link>
                  <router-link
                    v-else
                    :to="{ name: item.name, params: { permalink: item.permalink } }"
                    class="block stretched-link underline-hover leading-sm menu-item-link"
                  >
                    {{ item.text }}
                  </router-link>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { defineAsyncComponent, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'

import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import { useCategoriesStore } from '@/stores/CategoriesStore'

const { t } = useI18n()

const categoriesStore = useCategoriesStore()

const { getMixCategories, getProductCategories } = storeToRefs(categoriesStore)

const parents = [
  {
    id: 'mix',
    desktopText: t('mix_your_own_goodies'),
    mobileText: t('own_goodies'),
    title: t('mixes_title'),
    link: '',
    svg: defineAsyncComponent(() => import('@/components/svg/navbarMenu/MixItSvg.vue')),
    imageEvent: 'mixuj-birthday',
    isCurrent: false,
  },
  {
    id: 'ready',
    desktopText: t('show_all_categories'),
    mobileText: t('all_categories'),
    title: t('ready_mixes_title'),
    link: '',
    svg: defineAsyncComponent(() => import('@/components/svg/birds/BirdProducts.vue')),
    imageEvent: 'hotovky-birthday',
    isCurrent: true,
  },
]
const children = shallowRef([
  {
    id: 'mix',
    items: getMixCategories.value,
  },
  {
    id: 'ready',
    items: getProductCategories.value,
  },
])
</script>

<i18n>
  {
    "cs": {
    "all_categories": "Všechny kategorie",
    "i_want_ready_mix": "Chci hotový Mixit",
    "mix_your_own_goodies": "Namíchej si vlastní dobroty",
    "mixes_title": "Mixovat",
    "own_goodies": "Vlastní dobroty",
    "ready_mixes": "Hotové Mixit",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Zobrazit všechny kategorie"
},
    "sk": {
    "all_categories": "Všetky kategórie",
    "i_want_ready_mix": "Chcem hotový Mixit",
    "mix_your_own_goodies": "Namiešaj si vlastné dobroty",
    "mixes_title": "Mixovať",
    "own_goodies": "Vlastné dobroty",
    "ready_mixes": "Hotové Mixit",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Zobraziť všetky kategórie"
},
    "pl": {
    "all_categories": "Wszystkie kategorie",
    "i_want_ready_mix": "Chcę gotowy produkt",
    "mix_your_own_goodies": "Mixuj swoje smakołyki",
    "mixes_title": "Mixuj",
    "own_goodies": "Swoje smakołyki",
    "ready_mixes": "Gotowe produkty",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Pokaż wszystkie kategorie"
},
    "hu": {
    "all_categories": "Kategória megtekintése",
    "i_want_ready_mix": "Kész Mixitet szeretnék",
    "mix_your_own_goodies": "Mixelj saját finomságot",
    "mixes_title": "Mixelni",
    "own_goodies": "Saját finomságot",
    "ready_mixes": "Kész Mixit",
    "ready_mixes_title": "Termékek",
    "show_all_categories": "Minden kategória megtekintése"
},
    "de": {
    "all_categories": "Alle Kategorien",
    "i_want_ready_mix": "Ich möchte einen fertigen Mix",
    "mix_your_own_goodies": "Mix dir deine eigenen Leckereien",
    "mixes_title": "Mixen",
    "own_goodies": "Eigene Leckereien",
    "ready_mixes": "Fertig Mixit",
    "ready_mixes_title": "Produkte",
    "show_all_categories": "Alle Kategorien anzeigen"
},
    "at": {
    "all_categories": "Alle Kategorien",
    "i_want_ready_mix": "Ich möchte einen fertigen Mix",
    "mix_your_own_goodies": "Mix dir deine eigenen Leckereien",
    "mixes_title": "Mixen",
    "own_goodies": "Eigene Leckereien",
    "ready_mixes": "Fertig Mixit",
    "ready_mixes_title": "Produkte",
    "show_all_categories": "Alle Kategorien anzeigen"
},
    "ro": {
    "all_categories": "Toate categoriile",
    "i_want_ready_mix": "Mixit gata preparat",
    "mix_your_own_goodies": "Amestecă propriile bunătăți",
    "mixes_title": "Mixează",
    "own_goodies": "Propriile bunătăți",
    "ready_mixes": "Mixit gata preparat",
    "ready_mixes_title": "Produse",
    "show_all_categories": "Vizualizează toate categoriile"
},
    "fr": {
    "all_categories": "Toutes les catégories",
    "i_want_ready_mix": "Je veux un Mixit prêt",
    "mix_your_own_goodies": "Crée tes propres gourmandises",
    "mixes_title": "Crée ton mix",
    "own_goodies": "Propres gourmandises",
    "ready_mixes": "Mixit prêts",
    "ready_mixes_title": "Produits",
    "show_all_categories": "Afficher toutes les catégories"
},
    "it": {
    "all_categories": "Tutte le categorie",
    "i_want_ready_mix": "Voglio un Mixit già pronto",
    "mix_your_own_goodies": "Mescola le tue prelibatezze",
    "mixes_title": "Mescolare",
    "own_goodies": "Le proprie prelibatezze",
    "ready_mixes": "I Mixit pronti",
    "ready_mixes_title": "Prodotti",
    "show_all_categories": "Mostra tutte le categorie"
}
  }
</i18n>
